import debounce from 'debounce';
import { toast } from 'react-toastify';
import {
  addCartItem,
  removeCartItem,
  removeCartItemMessage,
  setCartItemAmount,
  setCartItemMessage,
} from './action';
import { recalculatePrices } from '../Prices/action';
import fetch from '../../../../js/api/fetch';
import isNumeric from '../../../../js/util/isNumeric';
import { addError } from '../MetaData/action';
import changeProductAmount from '../../functions/changeProductAmount';
import { updateRootState } from '../../functions/updateRootState';
import updateMiniCart from '../../../../js/checkout/cart/binder/updateMiniCart';
import type { TypedDispatch, TypedMiddleware } from '../store';

const debouncedSend = debounce(changeProductAmount, 500);
const createBaseTracker = async () =>
  import('@yoursurprise/segment-analytics').then(
    (Segment) => new Segment.Tracker(),
  );

const setGiftMessage = (
  cartItemId: string,
  message: string,
): Promise<Response> =>
  fetch('upsell/gift-card', {
    body: JSON.stringify({ cartItemId, message }),
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    method: 'POST',
  });

export const setGiftMessageMiddleware: TypedMiddleware =
  (storeApi) => (next) => (action) => {
    const { translations } = storeApi.getState();
    if (setCartItemMessage.match(action)) {
      setGiftMessage(action.payload.id, action.payload.message)
        .then(() => {
          toast(translations.upsell?.giftcard_saved, { type: 'success' });
        })
        .catch(() => {
          toast(translations.upsell?.giftcard_save_failed, { type: 'error' });
        });
    }

    return next(action);
  };

export const deleteGiftMessageMiddleware: TypedMiddleware =
  (storeApi) => (next) => (action) => {
    const { translations } = storeApi.getState();
    if (removeCartItemMessage.match(action)) {
      setGiftMessage(action.payload.id, '')
        .then(() => {
          toast(translations.upsell?.giftcard_removed, { type: 'success' });
        })
        .catch(() => {
          toast(translations.upsell?.giftcard_remove_failed, { type: 'error' });
        });
    }

    return next(action);
  };

export const setProductAmountMiddleware: TypedMiddleware =
  (storeApi) => (next) => (action) => {
    if (setCartItemAmount.match(action) && isNumeric(action.payload.amount)) {
      debouncedSend(
        storeApi.dispatch,
        action.payload.id,
        parseInt(action.payload.amount, 10),
      );

      const result = next(action);
      storeApi.dispatch(recalculatePrices());
      return result;
    }
    return next(action);
  };

const addUpsellProduct = async (
  dispatch: TypedDispatch,
  productId: number,
  amount: number,
  parentCartItemId: string,
  upsellGroupId: number,
): Promise<void> => {
  await fetch('upsell/add', {
    body: JSON.stringify({
      amount,
      cartItemId: parentCartItemId,
      productId,
      upsellGroupId,
    }),
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    },
    method: 'POST',
  });

  await updateRootState(dispatch);
};

export const addUpsellProductMiddleware: TypedMiddleware =
  (storeApi) => (next) => (action) => {
    if (addCartItem.match(action)) {
      const result = next(action);

      const { translations } = storeApi.getState();

      addUpsellProduct(
        storeApi.dispatch,
        action.payload.productId,
        action.payload.amount ?? 1,
        action.payload.parentCartItemId,
        action.payload.upsellGroupId,
      )
        .then(() => {
          updateMiniCart();
          toast(translations.upsell?.generic_upsell_added, { type: 'success' });
        })
        .then(() => {
          createBaseTracker()
            .then((tracker) => {
              tracker.track('Upsell Item Added', {
                label: String(action.payload.productId),
                parentCartItemId: action.payload.parentCartItemId,
                parentProductId: action.payload.parentProductId,
                personalised: false,
                productId: action.payload.productId,
                type: 'slider',
              });
            })
            .catch(() => null);
        })
        .catch(() => {
          toast(translations.upsell?.generic_upsell_add_failed, {
            type: 'error',
          });
        });

      storeApi.dispatch(recalculatePrices());
      return result;
    }

    return next(action);
  };

const removeProduct = async (
  dispatch: TypedDispatch,
  cartItemId: string,
): Promise<void> => {
  await fetch('checkout/removeProductFromCart', {
    body: JSON.stringify({ cartItemId }),
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    },
    method: 'DELETE',
  });

  await updateRootState(dispatch);
};

export const removeProductMiddleware: TypedMiddleware =
  (storeApi) => (next) => (action) => {
    if (removeCartItem.match(action)) {
      removeProduct(storeApi.dispatch, action.payload.id)
        .then(() => {
          updateMiniCart();
        })
        .catch(() => storeApi.dispatch(addError()));

      const result = next(action);

      storeApi.dispatch(recalculatePrices());

      return result;
    }

    return next(action);
  };
